import type { ConfigPlugin } from "./config";
import { Logger } from "./logger";

export interface FetchOptions {
  url: string;
  headers?: Record<string, string>;
  method?: string;
  body?: string;
  credentials?: "include" | "omit";
  signal?: AbortSignal;
}

export function createHttpClientPlugin(
  configPlugin: ConfigPlugin,
  logger: Logger
) {
  async function send(options: FetchOptions): Promise<Response | undefined> {
    try {
      const response = await fetch(options.url, options);
      if (!response.ok) {
        logger.error("request failed with http code:", response.status);
        return undefined;
      }
      return response;
    } catch (e) {
      // this is what happens when the request is blocked by ad blockers. Other errors could be caught here too, but no other options atm.
      if (e.message === "Failed to fetch") {
        return {
          // @ts-ignore
          stopRetry: true,
        };
      }

      // swallow SDK errors to prevent client from crashing
      logger.error("request failed", e);
      return undefined;
    }
  }

  async function retryRequest(request: () => Promise<Response | undefined>) {
    for (let i = 0; i < configPlugin.getConfig().maxRetries; i++) {
      const response = await request();

      // this means that the request was likely blocked by ad blockers (response in those cases comes back undefined)
      // so we should not retry
      // @ts-ignore
      if (response?.stopRetry === true) {
        return;
      }

      if (response?.ok) {
        return response;
      }
    }

    return undefined;
  }

  async function retrySend(
    options: FetchOptions
  ): Promise<Response | undefined> {
    const sendRequest = () => send(options);
    return retryRequest(sendRequest);
  }

  return {
    send,
    retrySend,
    retryRequest,
  };
}
